import {
  CONSENT_TRACKING_LABELS,
  NOTETAKER_POLICY_CAPTURE_MODE_LABELS,
  NotetakerPolicy,
  NotetakerPolicyBehavior,
  NotetakerPolicyBooleanSettingKey,
  NotetakerPolicyOwner,
  NotetakerPolicyRetentionValue,
  NotetakerPolicySetting,
  NotetakerPolicySettingKey,
  NotetakerPolicySettingValue,
  NotetakerPolicyUpdatePayload,
  RETENTION_MODE_LABELS,
  RetentionMode,
  toConsentTrackingMode,
  toNotetakerPolicyCaptureMode,
  toTranscriptVisibility,
  TRANSCRIPT_VISIBILITY_LABELS,
} from "@/schema"

import { SETTING_COPY } from "./setting-copy"

export interface DraftEntry {
  behavior: NotetakerPolicyBehavior
  value: NotetakerPolicySettingValue | null
}

export type PolicyDraft = Partial<Record<NotetakerPolicySettingKey, DraftEntry>>

export interface ResolvedSetting {
  value: NotetakerPolicySettingValue
  source: NotetakerPolicyOwner | null
  enforcer: NotetakerPolicyOwner | null
  locked: boolean
  localBehavior: NotetakerPolicyBehavior
  localValue: NotetakerPolicySettingValue | null
}

export function sameValue(a: NotetakerPolicySettingValue | null, b: NotetakerPolicySettingValue | null) {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function localEntry(setting: NotetakerPolicySetting, draft: PolicyDraft): DraftEntry {
  return draft[setting.key] ?? { behavior: setting.local_behavior, value: setting.local_value }
}

export function resolveSetting(setting: NotetakerPolicySetting, draft: PolicyDraft): ResolvedSetting {
  const local = localEntry(setting, draft)
  const ancestors = setting.inheritance.slice(0, -1)
  const enforcer = ancestors.find((level) => level.behavior === "enforce") ?? null

  if (enforcer) {
    return {
      value: enforcer.value ?? setting.system_default,
      source: enforcer,
      enforcer,
      locked: true,
      localBehavior: local.behavior,
      localValue: local.value,
    }
  }

  const self = setting.inheritance[setting.inheritance.length - 1]
  if (local.behavior !== "inherit" && local.value !== undefined) {
    return {
      value: local.value ?? setting.system_default,
      source: self,
      enforcer: null,
      locked: false,
      localBehavior: local.behavior,
      localValue: local.value,
    }
  }

  const nearest = [...ancestors].reverse().find((level) => level.behavior !== "inherit")
  return {
    value: nearest ? (nearest.value ?? setting.system_default) : setting.system_default,
    source: nearest ?? null,
    enforcer: null,
    locked: false,
    localBehavior: local.behavior,
    localValue: local.value,
  }
}

export function provenanceText(resolved: ResolvedSetting, owner: NotetakerPolicyOwner) {
  if (resolved.locked && resolved.enforcer) return `Enforced by ${resolved.enforcer.name}`
  if (resolved.localBehavior === "enforce") return "Enforced here"
  if (resolved.localBehavior === "prefer") return "Set here"
  if (!resolved.source || (resolved.source.type === owner.type && resolved.source.id === owner.id))
    return "System default"
  return `Inherited from ${resolved.source.name}`
}

function isRetention(value: NotetakerPolicySettingValue): value is NotetakerPolicyRetentionValue {
  return typeof value === "object" && value !== null && "mode" in value
}

export function formatValue(setting: NotetakerPolicySetting, value: NotetakerPolicySettingValue | null) {
  if (setting.key === "retention") {
    if (!isRetention(value)) return "Keep forever"
    const label = RETENTION_MODE_LABELS[value.mode]
    return value.mode === RetentionMode.EXPIRE_AFTER_DAYS && value.days ? `${label} (${value.days} days)` : label
  }
  if (setting.key === "announcement_text")
    return typeof value === "string" && value ? `“${value}”` : "Standard announcement"
  if (setting.key === "ai_notice_text") return typeof value === "string" && value ? `“${value}”` : "Standard wording"
  if (setting.key === "transcript_visibility")
    return TRANSCRIPT_VISIBILITY_LABELS[value === "hidden" ? "hidden" : "advisor"]
  if (setting.key === "capture_mode")
    return NOTETAKER_POLICY_CAPTURE_MODE_LABELS[toNotetakerPolicyCaptureMode(value) ?? "audio_video"]
  if (setting.key === "consent_tracking") return CONSENT_TRACKING_LABELS[toConsentTrackingMode(String(value)) ?? "off"]
  const copy = SETTING_COPY[setting.key]
  return value ? (copy.on ?? "On") : (copy.off ?? "Off")
}

function attestationRequired(value: NotetakerPolicySettingValue | null) {
  return value === "confirmed" || value === "inferred_and_confirmed"
}

export function consequenceNote(
  setting: NotetakerPolicySetting,
  before: NotetakerPolicySettingValue,
  after: NotetakerPolicySettingValue
) {
  if (sameValue(before, after)) return null
  if (setting.key === "retention") {
    return "Applies to recordings captured after this is saved. Existing recordings keep the retention they already have."
  }
  if (setting.key === "automatic_attendance" && before === true && after === false) {
    return "The notetaker stops joining new meetings automatically. Meetings that already have a planned notetaker keep it, and advisors can still invite it by hand."
  }
  if (setting.key === "announcement_required" && before === false && after === true) {
    return "Bots scheduled from now on play the announcement. Bots already scheduled keep the configuration they were created with."
  }
  if (setting.key === "ai_notice" && before === false && after === true) {
    return "Every AI summary and meeting prep in the books below shows the notice as soon as this is saved, including ones generated before today."
  }
  if (setting.key === "ai_notice" && before === true && after === false) {
    return "The notice disappears from existing AI summaries and meeting prep too. Nothing is stored on the meeting."
  }
  if (setting.key === "transcript_visibility" && after === "hidden") {
    return "Transcripts of meetings captured after this is saved are hidden from advisors in every book below: the transcript tab, downloads, transcript emails, exports, the public API, automation AI steps, and Book Chat. Existing transcripts are unchanged. AI summaries are unaffected."
  }
  if (setting.key === "transcript_visibility" && before === "hidden") {
    return "Transcripts of meetings captured after this is saved are visible to advisors. Transcripts captured while hidden stay hidden."
  }
  if (setting.key === "outside_recordings" && before === true && after === false) {
    return "Upload and import of outside recordings and transcripts is disabled for every book below. Recordings and transcripts already in Slant are kept."
  }
  if (setting.key === "phone_recording" && before === true && after === false) {
    return "Registered notetaker phone numbers stop recording (they stay saved), Call my phone is unavailable, and phone integrations stop importing call recordings and call transcripts. SMS and call logs keep syncing."
  }
  if (setting.key === "capture_mode" && after === "transcript_only") {
    return "Bots scheduled from now on record no audio or video and keep only a transcript. Recording from the web app and mobile app is turned off for every book under this level. Bots already scheduled keep the configuration they were created with."
  }
  if (setting.key === "consent_tracking" && !attestationRequired(before) && attestationRequired(after)) {
    return "Advisors must confirm client consent before AI notes for newly processed meetings are shown, and meeting-completed automations wait for that confirmation. Meetings processed before this change are not affected."
  }
  if (setting.key === "consent_tracking" && attestationRequired(before) && !attestationRequired(after)) {
    return "Pending confirmations unlock immediately."
  }
  return null
}

export function draftPayload(settings: NotetakerPolicySetting[], draft: PolicyDraft): NotetakerPolicyUpdatePayload {
  const payload: NotetakerPolicyUpdatePayload = {}

  for (const setting of settings) {
    const entry = draft[setting.key]
    if (!entry) continue
    const explicit = entry.behavior !== "inherit"

    switch (setting.key) {
      case "automatic_attendance":
        payload.automatic_attendance_behavior = entry.behavior
        if (explicit) payload.automatic_attendance = Boolean(entry.value)
        break
      case "skip_recurring_meetings":
        payload.skip_recurring_meetings_behavior = entry.behavior
        if (explicit) payload.skip_recurring_meetings = Boolean(entry.value)
        break
      case "skip_internal_meetings":
        payload.skip_internal_meetings_behavior = entry.behavior
        if (explicit) payload.skip_internal_meetings = Boolean(entry.value)
        break
      case "internal_meetings_private":
        payload.internal_meetings_private_behavior = entry.behavior
        if (explicit) payload.internal_meetings_private = Boolean(entry.value)
        break
      case "announcement_required":
        payload.announcement_required_behavior = entry.behavior
        if (explicit) payload.announcement_required = Boolean(entry.value)
        break
      case "transcript_email":
        payload.transcript_email_behavior = entry.behavior
        if (explicit) payload.transcript_email = Boolean(entry.value)
        break
      case "outside_recordings":
        payload.outside_recordings_behavior = entry.behavior
        if (explicit) payload.outside_recordings = Boolean(entry.value)
        break
      case "phone_recording":
        payload.phone_recording_behavior = entry.behavior
        if (explicit) payload.phone_recording = Boolean(entry.value)
        break
      case "announcement_text":
        payload.announcement_text_behavior = entry.behavior
        if (explicit) payload.announcement_text = typeof entry.value === "string" && entry.value ? entry.value : null
        break
      case "ai_notice":
        payload.ai_notice_behavior = entry.behavior
        if (explicit) payload.ai_notice = Boolean(entry.value)
        break
      case "ai_notice_text":
        payload.ai_notice_text_behavior = entry.behavior
        if (explicit) payload.ai_notice_text = typeof entry.value === "string" && entry.value ? entry.value : null
        break
      case "transcript_visibility": {
        payload.transcript_visibility_behavior = entry.behavior
        const visibility = typeof entry.value === "string" ? toTranscriptVisibility(entry.value) : undefined
        if (explicit) payload.transcript_visibility = visibility ?? "advisor"
        break
      }
      case "capture_mode": {
        payload.capture_mode_behavior = entry.behavior
        const mode = toNotetakerPolicyCaptureMode(entry.value)
        if (explicit && mode) payload.capture_mode = mode
        break
      }
      case "retention":
        payload.retention_behavior = entry.behavior
        if (explicit && isRetention(entry.value)) {
          payload.retention_mode = entry.value.mode
          payload.retention_days = entry.value.mode === RetentionMode.EXPIRE_AFTER_DAYS ? entry.value.days : null
        }
        break
      case "consent_tracking": {
        payload.consent_tracking_behavior = entry.behavior
        const mode = toConsentTrackingMode(String(entry.value))
        if (explicit && mode) payload.consent_tracking = mode
        break
      }
    }
  }

  return payload
}

export function blockedBy(policy: NotetakerPolicy | undefined, key: NotetakerPolicyBooleanSettingKey): string | null {
  const setting = policy?.settings.find((candidate) => candidate.key === key)
  if (!setting || setting.effective_value !== false) return null
  return setting.source?.name ?? "your firm"
}

export function retentionModeOf(policy: NotetakerPolicy | undefined): RetentionMode | undefined {
  const retention = policy?.settings.find((setting) => setting.key === "retention")
  return retention && isRetention(retention.effective_value) ? retention.effective_value.mode : undefined
}
